import { localeRoutes } from './intl';

export const relativeRoutes = {
  address: 'address',
  addCard: 'add-card',
  addGiftCard: 'add-gift-card',
  transferGiftCard: 'transfer-gift-card',
  locationService: 'location-service',
  noDeliveryStores: 'no-delivery-stores',
  deliveryStoresClosed: 'delivery-stores-closed',
  orderPayment: 'payment',
  pickupMode: 'pickup-mode',
  serviceMode: 'service-mode',
  serviceModeCatering: 'service-mode-catering',
  serviceModeCateringDelivery: 'service-mode-catering-delivery',
  serviceModeCateringPickup: 'service-mode-catering-pickup',
  store: 'store',
  sharedRolls: 'send',
};

export const routes = {
  about: '/about',
  aboutApp: '/about-app',
  addCard: `/account/payment/${relativeRoutes.addCard}`,
  addGiftCard: `/account/payment/${relativeRoutes.addGiftCard}`,
  address: `/store-locator/${relativeRoutes.address}`,
  authChallengeJwt: '/auth-challenge-jwt',
  base: '/',
  cart: '/cart',
  cartAddCard: `/cart/${relativeRoutes.addCard}`,
  cartTransferGiftCard: `/cart/${relativeRoutes.transferGiftCard}`,
  cardAddGiftCard: `/cart/${relativeRoutes.addGiftCard}`,
  catering: `/catering`,
  communication: `/communication-preferences`,
  confirmJwt: '/confirm-jwt',
  confirmOtp: '/confirm-otp',
  delivery: '/delivery',
  deliveryStoresClosed: `/store-locator/${relativeRoutes.deliveryStoresClosed}`,
  diagnostics: '/diagnostics',
  info: '/account/info',
  locationService: `/store-locator/${relativeRoutes.locationService}`,
  menu: '/menu',
  missingItems: '/missing-items',
  missingOrder: '/missing-order',
  noDeliveryStores: `/store-locator/${relativeRoutes.noDeliveryStores}`,
  notFound: '/not-found',
  notFound404: '/not-found/404',
  nutritionExplorer: '/nutrition-explorer',
  offers: '/offers',
  orderConfirmation: '/order-confirmation',
  storeOrderConfirmation: '/store-order-confirmation',
  orders: '/account/orders',
  payment: '/account/payment',
  pickupMode: `/store-locator/${relativeRoutes.pickupMode}`,
  reportOrderIssue: '/digitalorderhelp',
  rewardsOnboarding: '/rewards-onboarding',
  reusables: '/reusable',
  scaryPlaces: '/scaryplaces',
  serviceMode: `/store-locator/${relativeRoutes.serviceMode}`,
  serviceModeCatering: `/store-locator/${relativeRoutes.serviceModeCatering}`,
  serviceModeCateringDelivery: `/store-locator/${relativeRoutes.serviceModeCateringDelivery}`,
  serviceModeCateringPickup: `/store-locator/${relativeRoutes.serviceModeCateringPickup}`,
  signOut: '/signout',
  store: `/store-locator/${relativeRoutes.store}`,
  support: '/support',
  supportCategories: '/support/categories',
  supportCategory: '/support/categories/:categoryId',
  supportOrders: '/support/orders',
  supportForm: '/support/form',
  timCard: '/account/tim-card',
  timsRewards: '/tims-rewards',
  transferGiftCard: `/account/payment/${relativeRoutes.transferGiftCard}`,
  trending: '/trending',
  contactUs: 'contact-us',
  offerActivate: '/activate-offer',

  //TODO: this route is a static page in sanity. Ensure it exists in data set

  // Default localizable routes
  account: localeRoutes.en.account,
  acceptGift: localeRoutes.en.acceptGift,
  sendGift: localeRoutes.en.sendGift,
  signUp: localeRoutes.en.signUp,
  signIn: localeRoutes.en.signIn,
  storeLocator: localeRoutes.en.storeLocator,
  quiz: localeRoutes.en.quiz,
  foundation: '/foundation',
};

// collection of routes only used in kiosk app
export const kioskRoutes = {
  admin: '/admin',
  offerEntry: '/offer-entry',
  scanRewards: '/scan-rewards',
  scanRewardsInput: '/scan-rewards-input',
  relative: {
    cashConfirmation: 'cash-confirmation',
    digitalConfirmation: 'digital-confirmation',
    giftCardOption: 'gift-card-option',
    giftCardScanPayment: 'gift-card-scan-payment',
    nameInput: 'name-input',
    pairingModal: 'pairing-modal',
    payment: 'payment',
    paymentModal: 'payment-modal',
    paymentOption: 'payment-option',
    pickupMode: 'pickup-mode',
  },
};

// brand-specific routes used to determine whether to display service mode header
export const routesRelevantToServiceModeHeaderDisplay = {
  default: 'store-locator',
  plk: 'store-locator',
  bk: 'store-locator',
  th: 'menu',
};

// brand-specific routes used to determine whether to display service mode header
export const routesForBottomServiceModeDisplay = [
  { route: routes.base, exact: true },
  { route: routes.menu, exact: false },
  { route: routes.orders, exact: false },
  { route: routes.offers, exact: false },
];

export function goBack({ navigate, location }) {
  const pathParts = location.pathname.split('/');
  pathParts.length = pathParts.length - 1;
  navigate(pathParts.join('/'));
}

export function goToMenu({ navigate }) {
  navigate(routes.menu);
}

/**
 * Maps strings to an object, string in format `item-4321-2` become {item-4321: "2"}
 *
 * @param {Array<string>} [params] array of kebab case strings you want to map to an object
 *
 * @returns {Record<string, string>} Object with each key and value being a single param
 */
export const mapParamsToObject = (params = []) =>
  params.reduce((map, param) => {
    const lastIndex = param.lastIndexOf('-');
    const key = lastIndex !== -1 ? param.substring(0, lastIndex) : param;
    const value = lastIndex !== -1 ? param.substring(lastIndex + 1) : '';
    map[key] = value;
    return map;
  }, {});

export function extractRefFromUrl({ '*': splat }, subtrahend = -1) {
  const paths = (splat || '').split('/');
  const ref = paths[paths.length + subtrahend];
  return ref;
}

/**
 * Creates a url for a cartItem, recursively mapping over its children
 *
 * @param {Record<string, string>} [newPickerSelections] extra options that can be passed in
 *
 * @returns {URLSearchParams} Url search param with all the picker params
 */
export const createPickerSelectionParams = newPickerSelections => {
  return Object.keys(newPickerSelections).reduce((urlParams, aspectKey) => {
    urlParams.append(`picker`, `${aspectKey}-${newPickerSelections[aspectKey]}`);
    return urlParams;
  }, new URLSearchParams(''));
};

/**
 * This function will recursively map over an cartEntries and
 * return a flat array of strings that can be used as url params. You can
 * optionally provide a formatting function to control what those values
 * are. Also can optionally provide a key to recursively look for in each object.
 *
 * @param {ICartEntry[]} childrenArray array containing objects with recursive children
 * @param {(ICartEntry) => string} [formatFunc] a function to format the params that gets passed the entire child
 * @param {string} [key] the objects key to look for children
 *
 * @returns {Array<string>} Flat array of strings to use as params in a url
 */
export function mapCartEntriesToParams(
  childrenArray,
  formatFunc = child => `${child._id}-${child.quantity}`,
  key = 'children'
) {
  const flatArray = [];
  childrenArray.forEach(child => {
    flatArray.push(formatFunc(child));
    if (child[key]?.length) {
      flatArray.push(...mapCartEntriesToParams(child[key]));
    }
  });
  return flatArray;
}

/**
 * Creates a url for a cartItem, recursively mapping over its children
 *
 * @param {{ id?: string; cartId?: string; children?: ICartEntry[], isOffer?: boolean; quantity?: number; pickerSelections?: Record<string, string>; location?: Partial<Location>}} [options] extra options that can be passed in
 *
 * @returns {string} Url containing cart items ids and children
 */
export function generateCartItemUrl({
  id,
  cartId,
  children,
  quantity,
  pickerSelections,
  isOffer,
  location = window.location,
} = {}) {
  const urlParams = new URLSearchParams('');

  const pathname = location.pathname;
  if (isOffer) {
    urlParams.append('isOffer', true);
  }
  if (cartId) {
    urlParams.append('cartId', cartId);
  }
  if (id) {
    urlParams.append('_id', id);
  }
  if (pickerSelections) {
    const pickerParams = createPickerSelectionParams(pickerSelections);
    pickerParams.forEach(param => {
      urlParams.append('picker', param);
    });
  }
  if (children) {
    const childrenParams = mapCartEntriesToParams(children);
    childrenParams.forEach(param => {
      urlParams.append('children', param);
    });
  }
  if (quantity) {
    urlParams.append('quantity', quantity);
  }

  const queryString = urlParams.toString() ? `?${urlParams.toString()}` : '';

  return `${pathname}${queryString}`;
}

export {
  localeRoutes,
  getAllLocalizedVersionsOfRoute,
  getLocalizedRoute,
  getInCodeLocalizedRouteForPath,
  getStaticPagesLocalizedRouteForPath,
} from './intl';

// These are routes that are safe for appUpdates

export const UPDATE_ALLOWED_ROUTES = [routes.menu, routes.about];
